/**
 * Paying for self-funding access inside the consumer flow: the embedded Stripe Checkout for the
 * session the paywall created, then, on return, confirming the payment before moving on. See
 * `useSelfFundCheckoutStep` for the modes.
 */
import { Alert, AlertDescription, AlertTitle, Button, Spinner } from '@design-system';
import { WAG_SUPPORT_EMAIL } from '@shared';
import { D2cCheckoutPaymentBox } from '@/features/client-onboarding/components/d2c-checkout-payment-box';
import { StepFrame } from '../components/step-frame';
import { useSelfFundCheckoutStep } from '../hooks/use-self-fund-checkout-step';
import type { ConsumerStepProps } from './step-props';

export function SelfFundCheckoutStep({ clientId }: ConsumerStepProps) {
  const { mode, checkout, confirmation, back, leaveUnconfirmed, continueOn } = useSelfFundCheckoutStep(clientId);

  if (mode === 'confirming') {
    return (
      <StepFrame
        step="self-fund-checkout"
        title="Confirming your payment"
        onBack={confirmation.hasTimedOut ? leaveUnconfirmed : undefined}
      >
        {confirmation.hasTimedOut ? (
          <Alert>
            <AlertTitle>We’re still confirming your payment</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-2">
              This can take a minute. If it doesn’t clear, email {WAG_SUPPORT_EMAIL}.
              <Button
                variant="outline"
                size="sm"
                onClick={confirmation.retry}
              >
                Check again
              </Button>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="flex items-center gap-3 py-8">
            <Spinner className="size-6" />
            <p className="text-foreground">Confirming your payment…</p>
          </div>
        )}
      </StepFrame>
    );
  }

  if (mode === 'already-active') {
    return (
      <StepFrame
        step="self-fund-checkout"
        title="You already have self-funding access"
        subtitle="There’s nothing to pay. Your access is still active."
        onBack={back}
        primary={{ label: 'Continue', onClick: continueOn }}
      />
    );
  }

  return (
    <StepFrame
      step="self-fund-checkout"
      title="Pay securely"
      subtitle="One-time payment for 365 days of access. No automatic renewal or recurring charges."
      onBack={back}
    >
      <D2cCheckoutPaymentBox
        clientSecret={checkout.clientSecret}
        isPending={checkout.isPending}
        isError={checkout.isError}
        onRetry={checkout.retry}
        price={checkout.price}
        access="365 days of access"
        noRenewal
      />
    </StepFrame>
  );
}
